import { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Collapse,
  Box,
  LinearProgress,
} from "@mui/material";
import { InboxOutlined as InboxOutlinedIcon } from "@mui/icons-material";
import { useStableQuery } from "../../hooks/useStableQuery";
import { api } from "../../../../convex/_generated/api";
import { relativeTime } from "../../utils/relativeTime";

type StatusFilter = "all" | "queued" | "sent" | "completed" | "failed" | "expired";

const FILTER_CHIPS: { label: string; value: StatusFilter }[] = [
  { label: "All", value: "all" },
  { label: "Queued", value: "queued" },
  { label: "Sent", value: "sent" },
  { label: "Completed", value: "completed" },
  { label: "Failed", value: "failed" },
  { label: "Expired", value: "expired" },
];

const commandStatusColors: Record<string, "info" | "primary" | "success" | "error" | "default"> = {
  queued: "info",
  sent: "primary",
  completed: "success",
  failed: "error",
  expired: "default",
};

/** Map agentId to a human-friendly label. */
function agentDisplayName(agentId: string | undefined): string {
  if (!agentId) return "--";
  // Match patterns like "agent_3_qb_posting" -> "Agent 3"
  const match = agentId.match(/agent[_-]?(\d+)/i);
  if (match) return `Agent ${match[1]}`;
  return agentId;
}

export function QBCommandQueue() {
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const queryArgs = filter === "all" ? { limit: 200 } : { status: filter, limit: 200 };
  const commands = useStableQuery(api.qbDesktopQueue.listCommands, queryArgs, undefined);

  const loading = commands === undefined;
  const allCommands = commands ?? [];

  // Client-side pagination
  const paginatedCommands = allCommands.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleFilterChange = (value: StatusFilter) => {
    setFilter(value);
    setPage(0);
    setExpandedRow(null);
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>Command Queue</Typography>

        {/* Filter chips */}
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
          {FILTER_CHIPS.map((chip) => (
            <Chip
              key={chip.value}
              label={chip.label}
              variant={filter === chip.value ? "filled" : "outlined"}
              color={filter === chip.value ? "primary" : "default"}
              onClick={() => handleFilterChange(chip.value)}
              sx={{ cursor: "pointer" }}
            />
          ))}
        </Stack>

        {/* Loading state */}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Table */}
        {!loading && allCommands.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <InboxOutlinedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {filter === "all"
                ? "No commands yet"
                : `No ${filter} commands`}
            </Typography>
            {filter === "all" && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Commands will appear here when agents start syncing with QuickBooks Desktop.
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 160 }}>Type</TableCell>
                    <TableCell sx={{ width: 120 }}>Status</TableCell>
                    <TableCell sx={{ width: 140 }}>Time</TableCell>
                    <TableCell sx={{ width: 140 }}>Agent</TableCell>
                    <TableCell sx={{ width: 100 }}>Retries</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCommands.map((cmd: any) => (
                    <CommandRow
                      key={cmd._id}
                      command={cmd}
                      expanded={expandedRow === cmd._id}
                      onToggle={() =>
                        setExpandedRow(expandedRow === cmd._id ? null : cmd._id)
                      }
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={allCommands.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * A single command table row with expandable detail.
 */
function CommandRow({
  command,
  expanded,
  onToggle,
}: {
  command: any;
  expanded: boolean;
  onToggle: () => void;
}) {
  // Lazily load full detail only when expanded
  const detail = useStableQuery(
    api.qbDesktopQueue.getCommandDetail,
    expanded ? { commandId: command._id } : "skip",
    null,
  );

  const retryCount = command.retryCount ?? 0;

  return (
    <>
      <TableRow
        hover
        onClick={onToggle}
        sx={{ cursor: "pointer", "& > td": { borderBottom: expanded ? "none" : undefined } }}
      >
        <TableCell>
          <Typography variant="body2">{command.commandType}</Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={command.status.charAt(0).toUpperCase() + command.status.slice(1)}
            size="small"
            color={commandStatusColors[command.status] ?? "default"}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {relativeTime(command.createdAt)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{agentDisplayName(command.requestedBy)}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {retryCount > 0 ? `Retry ${retryCount}/3` : "--"}
          </Typography>
        </TableCell>
      </TableRow>

      {/* Expandable detail row */}
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0, border: expanded ? undefined : "none" }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2 }}>
              {detail ? (
                <Stack spacing={1.5}>
                  {detail.errorMessage && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Error
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          fontFamily: '"IBM Plex Mono", monospace',
                          fontSize: 12,
                          color: "text.secondary",
                          background: "var(--c-overlay-2)",
                          borderRadius: 2,
                          p: 1.5,
                          m: 0,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {detail.errorMessage}
                      </Box>
                    </Box>
                  )}
                  {detail.qbXml && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Request XML
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          fontFamily: '"IBM Plex Mono", monospace',
                          fontSize: 12,
                          color: "text.secondary",
                          background: "var(--c-overlay-2)",
                          borderRadius: 2,
                          p: 1.5,
                          m: 0,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          maxHeight: 200,
                          overflow: "auto",
                        }}
                      >
                        {detail.qbXml}
                      </Box>
                    </Box>
                  )}
                  {detail.responseXml && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Response XML
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          fontFamily: '"IBM Plex Mono", monospace',
                          fontSize: 12,
                          color: "text.secondary",
                          background: "var(--c-overlay-2)",
                          borderRadius: 2,
                          p: 1.5,
                          m: 0,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          maxHeight: 200,
                          overflow: "auto",
                        }}
                      >
                        {detail.responseXml}
                      </Box>
                    </Box>
                  )}
                  {!detail.errorMessage && !detail.qbXml && !detail.responseXml && (
                    <Typography variant="body2" color="text.secondary">No detail available</Typography>
                  )}
                </Stack>
              ) : (
                <LinearProgress />
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
